import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VENTAGE_IMG =
  "https://static.prod-images.emergentagent.com/jobs/6eda8702-546d-4ee1-94a6-0616cf049749/images/01a26e1d1ebbf367b97c2f4ea9c69a7f62b407d4e0ecf8ba1046b6c4a1544438.png";

const TYPES = [
  { value: "estimation", label: "Estimation gratuite" },
  { value: "paiement", label: "Paiement échelonné" },
  { value: "contact", label: "Contact général" },
];

export default function ContactEstimation() {
  const [type, setType] = useState("estimation");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    brand: "",
    model: "",
    year: "",
    condition: "Bon",
    has_papers: false,
    has_box: false,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const isEstimation = type === "estimation";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Veuillez remplir nom et email.");
      return;
    }
    if (isEstimation && !form.brand) {
      toast.error("Veuillez indiquer la marque de votre montre.");
      return;
    }
    if (!isEstimation && !form.message) {
      toast.error("Veuillez écrire un message.");
      return;
    }
    setSubmitting(true);
    try {
      if (isEstimation) {
        await axios.post(`${API}/estimation`, {
          name: form.name,
          email: form.email,
          phone: form.phone,
          brand: form.brand,
          model: form.model,
          year: form.year,
          condition: form.condition,
          has_papers: form.has_papers,
          has_box: form.has_box,
          message: form.message,
        });
      } else {
        await axios.post(`${API}/contact`, {
          name: form.name,
          email: form.email,
          phone: form.phone,
          message: `[${type === "paiement" ? "Paiement échelonné" : "Contact"}] ${form.message}`,
        });
      }
      toast.success(
        "Demande envoyée. Notre équipe vous recontacte sous 24h."
      );
      setForm({
        name: "",
        email: "",
        phone: "",
        brand: "",
        model: "",
        year: "",
        condition: "Bon",
        has_papers: false,
        has_box: false,
        message: "",
      });
    } catch {
      toast.error("Erreur lors de l'envoi. Réessayez ou appelez-nous.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#001226]/30 px-0 py-3 text-[#001226] text-sm focus:border-accent transition-colors placeholder:text-[#001226]/40";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#F8F4EC] py-16 md:py-32 border-t border-[#001226]/10"
    >
      {/* Both #vente and #estimation anchor here too */}
      <span id="vente" className="block -mt-24 pt-24" aria-hidden />
      <span id="estimation" className="block -mt-24 pt-24" aria-hidden />

      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Left: heading + infos */}
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <p className="text-[10px] uppercase tracking-[0.4em] text-accent mb-6">
              Contact & Estimation
            </p>
            <h2 className="font-serif font-light text-[#001226] text-4xl sm:text-5xl lg:text-6xl leading-[1] tracking-tight">
              Parlons de
              <br />
              <span className="italic">votre projet.</span>
            </h2>
            <p className="mt-8 text-[#1E2A40] leading-relaxed font-light max-w-md">
              Estimation, paiement échelonné ou simple demande — notre conseiller
              vous répond confidentiellement sous 24h ouvrées.
            </p>

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div className="flex items-start gap-4">
                <MapPin size={18} className="text-accent mt-1 shrink-0" />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#5C6373] mb-2">Boutique</p>
                  <p className="text-[#001226] font-light leading-relaxed text-sm">
                    Avenue de Cour 82<br />1007 Lausanne, Suisse
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Clock size={18} className="text-accent mt-1 shrink-0" />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#5C6373] mb-2">Horaires</p>
                  <p className="text-[#001226] font-light leading-relaxed text-sm">
                    Lun–Ven · 10h–16h<br />Samedi · sur rendez-vous
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone size={18} className="text-accent mt-1 shrink-0" />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#5C6373] mb-2">Téléphone</p>
                  <a href="tel:+41788073799" className="text-[#001226] font-light hover:text-accent transition-colors block text-sm">078 807 37 99</a>
                  <a href="tel:+41216162123" className="text-[#001226] font-light hover:text-accent transition-colors block text-sm">021 616 21 23</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Mail size={18} className="text-accent mt-1 shrink-0" />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#5C6373] mb-2">Email</p>
                  <a href="mailto:contact@adjuge-vendu.ch" className="text-[#001226] font-light hover:text-accent transition-colors text-sm">contact@adjuge-vendu.ch</a>
                </div>
              </div>
            </div>

            <div className="mt-12 hidden lg:block aspect-[4/3] overflow-hidden">
              <img
                src={VENTAGE_IMG}
                alt="Boutique Adjugé-Vendu"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Right: unified form */}
          <form
            onSubmit={handleSubmit}
            data-testid="contact-estimation-form"
            className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6"
          >
            {/* Type selector */}
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.3em] text-[#5C6373] mb-3 block">
                Type de demande
              </label>
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-3">
                {TYPES.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setType(t.value)}
                    data-testid={`form-type-${t.value}`}
                    className={`px-4 sm:px-5 py-3 text-[10px] sm:text-[11px] uppercase tracking-[0.22em] sm:tracking-[0.25em] border transition-colors text-center ${
                      type === t.value
                        ? "bg-accent text-white border-accent"
                        : "bg-transparent text-[#001226] border-[#001226]/30 hover:border-accent hover:text-accent"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.3em] text-[#5C6373]">Nom complet *</label>
              <input data-testid="form-name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Jean Dupont" className={inputCls} required />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.3em] text-[#5C6373]">Email *</label>
              <input data-testid="form-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="vous@exemple.ch" className={inputCls} required />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.3em] text-[#5C6373]">Téléphone</label>
              <input data-testid="form-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+41 79 …" className={inputCls} />
            </div>

            {/* Estimation-only fields */}
            {isEstimation && (
              <>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-[#5C6373]">Marque *</label>
                  <input data-testid="form-brand" value={form.brand} onChange={(e) => update("brand", e.target.value)} placeholder="Rolex, Patek Philippe…" className={inputCls} required />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-[#5C6373]">Modèle</label>
                  <input data-testid="form-model" value={form.model} onChange={(e) => update("model", e.target.value)} placeholder="Submariner, Nautilus…" className={inputCls} />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-[#5C6373]">Année</label>
                  <input data-testid="form-year" value={form.year} onChange={(e) => update("year", e.target.value)} placeholder="2018" className={inputCls} />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-[#5C6373]">État</label>
                  <select data-testid="form-condition" value={form.condition} onChange={(e) => update("condition", e.target.value)} className={`${inputCls} appearance-none`}>
                    <option value="Neuf">Neuf</option>
                    <option value="Excellent">Excellent</option>
                    <option value="Bon">Bon</option>
                    <option value="Usagé">Usagé</option>
                  </select>
                </div>
                <div className="md:col-span-2 flex gap-8 mt-2">
                  <label className="flex items-center gap-3 text-sm text-[#1E2A40] cursor-pointer">
                    <input data-testid="form-papers" type="checkbox" checked={form.has_papers} onChange={(e) => update("has_papers", e.target.checked)} className="accent-[#F06020] w-4 h-4" />
                    Papiers d'origine
                  </label>
                  <label className="flex items-center gap-3 text-sm text-[#1E2A40] cursor-pointer">
                    <input data-testid="form-box" type="checkbox" checked={form.has_box} onChange={(e) => update("has_box", e.target.checked)} className="accent-[#F06020] w-4 h-4" />
                    Boîte d'origine
                  </label>
                </div>
              </>
            )}

            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.3em] text-[#5C6373]">
                Message {!isEstimation && "*"}
              </label>
              <textarea
                data-testid="form-message"
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                rows={5}
                placeholder={
                  isEstimation
                    ? "Décrivez votre montre, son historique, vos disponibilités…"
                    : type === "paiement"
                    ? "Quelle montre vous intéresse ? Budget mensuel souhaité ?"
                    : "Comment pouvons-nous vous aider ?"
                }
                className={`${inputCls} resize-none`}
                required={!isEstimation}
              />
            </div>

            <div className="md:col-span-2 mt-6 flex flex-wrap items-center justify-between gap-6">
              <p className="text-xs text-[#5C6373] max-w-sm leading-relaxed">
                Réponse confidentielle sous 24h ouvrées. Sans engagement.
              </p>
              <button
                type="submit"
                disabled={submitting}
                data-testid="form-submit-button"
                className="inline-flex items-center gap-3 px-10 py-4 bg-accent text-white text-[11px] uppercase tracking-[0.25em] hover:bg-accent-dark transition-colors disabled:opacity-50"
              >
                {submitting ? "Envoi en cours…" : "Envoyer ma demande"}
                <span aria-hidden>→</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
